/**
 * Runtime shape guards for values that cross the network boundary.
 *
 * API responses are typed at compile time but not at runtime: a stale backend
 * build, a demo/stub deployment or an unexpected (partial) payload can omit a
 * field the client types promise. Iterating such a value tears down the whole
 * React tree with "Cannot read properties of undefined (reading 'map')", so
 * every render that walks network data goes through these helpers instead of
 * trusting the types.
 *
 * The helpers return the original array when it already is one, so identity is
 * preserved and memoised selectors keep working.
 */

/** An array, or a fresh empty array when the payload is missing/malformed. */
export function asArray<T>(value: readonly T[] | null | undefined): T[];
export function asArray(value: unknown): unknown[];
export function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/** Like `asArray`, but drops `null`/`undefined` entries (lists of objects). */
export function asDefinedArray<T>(value: readonly (T | null | undefined)[] | null | undefined): T[];
export function asDefinedArray(value: unknown): unknown[];
export function asDefinedArray(value: unknown): unknown[] {
  return (Array.isArray(value) ? value : []).filter((item) => item !== null && item !== undefined);
}

/**
 * A finite number, or `fallback`. Numeric strings are accepted because Postgres
 * `numeric` columns arrive as strings over JSON.
 */
export function asNumber(value: unknown, fallback = 0): number {
  if (typeof value === 'number') return Number.isFinite(value) ? value : fallback;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
  return fallback;
}

/** A string, or `fallback`. Numbers/booleans are stringified for labels. */
export function asString(value: unknown, fallback = ''): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return fallback;
}
